import ContentContainer from '@/components/containers/contentContainer';
import { api } from '@/utils/api';
import {
  formatVoucherDiscount,
  getMyVoucherStatus,
  type MyVoucherStatus,
} from '@/utils/vouchers';
import type { MyVoucher } from '@/types/jobPaymentTypes';
import { CircularProgress } from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from "@/utils/appToast";

function statusLabelKey(status: MyVoucherStatus): string {
  switch (status) {
    case 'active':
      return 'my_vouchers_status_active';
    case 'inactive':
      return 'my_vouchers_status_inactive';
    case 'expired':
      return 'my_vouchers_status_expired';
    case 'upcoming':
      return 'my_vouchers_status_upcoming';
    default:
      return 'my_vouchers_status_inactive';
  }
}

function statusClass(status: MyVoucherStatus): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'upcoming':
      return 'bg-blue-100 text-blue-800';
    case 'expired':
      return 'bg-gray-100 text-gray-600';
    case 'inactive':
    default:
      return 'bg-red-100 text-red-700';
  }
}

export default function MyVouchersPage() {
  const { t } = useTranslation();
  const [vouchers, setVouchers] = useState<MyVoucher[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.getMyVouchers();
        if (cancelled) return;
        if (res.success) {
          setVouchers(res.data || []);
        } else {
          setVouchers([]);
          toast.error(res.message || t('my_vouchers_load_error'));
        }
      } catch {
        if (!cancelled) {
          setVouchers([]);
          toast.error(t('my_vouchers_load_error'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <ContentContainer>
      <div className="w-full max-w-3xl mx-auto mt-6 px-2">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('my_vouchers')}</h1>
        <p className="text-sm text-gray-600 mb-6">{t('my_vouchers_intro')}</p>

        {loading ? (
          <div className="flex justify-center py-16">
            <CircularProgress />
          </div>
        ) : vouchers.length === 0 ? (
          <div className="bg-white border rounded-2xl p-8 text-center text-gray-600">
            {t('my_vouchers_empty')}
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {vouchers.map((voucher) => {
              const status = getMyVoucherStatus(voucher);
              return (
                <li
                  key={voucher._id}
                  className="bg-white border rounded-2xl p-4 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-lg font-bold text-gray-900 tracking-wide">
                        {voucher.code}
                      </p>
                      <p className="text-sm text-gray-700">
                        {t('voucher_discount')}: {formatVoucherDiscount(voucher)}
                      </p>
                    </div>
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusClass(status)}`}
                    >
                      {t(statusLabelKey(status))}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('my_vouchers_validity')}:{' '}
                    {moment(voucher.validFrom).format('MMM D, YYYY')} –{' '}
                    {moment(voucher.validTo).format('MMM D, YYYY')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 capitalize">
                    {t('my_vouchers_audience')}: {t(`my_vouchers_audience_${voucher.audience}`)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </ContentContainer>
  );
}
